import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  CalendarClock,
  Check,
  FileDown,
  FileSpreadsheet,
  Loader2,
  LogOut,
  Mail,
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
  Wallet,
  Waves,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { API, logoutRequest, useUser } from "@/lib/auth";
import { ContactsTab } from "@/components/panel/ContactsTab";
import { BusinessSettings, logoUrl } from "@/components/panel/BusinessSettings";

const fmt = (n) =>
  `${n < 0 ? "−" : ""}₺${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(Math.abs(n))}`;

const today = () => new Date().toISOString().slice(0, 10);

const CATEGORIES = {
  gelir: ["Satış", "Hizmet", "Tahsilat", "Diğer"],
  gider: ["Kira", "Maaş", "Tedarik", "Pazarlama", "Vergi", "Abonelik", "Diğer"],
};

const DONUT_COLORS = [
  "#10B981",
  "#00FF87",
  "#38BDF8",
  "#F59E0B",
  "#A78BFA",
  "#F472B6",
  "#EF4444",
];

const inputCls =
  "rounded-xl border border-white/12 bg-[#07090E] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60";

const TABS = [
  { id: "islemler", label: "İşlemler", testid: "panel-tab-transactions" },
  { id: "hesaplar", label: "Hesaplar", testid: "panel-tab-accounts" },
  { id: "yaklasan", label: "Yaklaşan", testid: "panel-tab-upcoming" },
  { id: "cari", label: "Cari", testid: "panel-tab-contacts" },
];

const ContactSelect = ({ value, onChange, contacts, type, testid }) => {
  const list = type ? contacts.filter((c) => c.type === type) : contacts;
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} w-full`}
    >
      <option value="">Cari hesap seç (isteğe bağlı)</option>
      {list.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name} — {c.type === "musteri" ? "Müşteri" : "Tedarikçi"}
        </option>
      ))}
    </select>
  );
};

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel rounded-xl border border-white/10 px-4 py-3 text-xs">
      <div className="mb-1.5 font-semibold text-slate-200">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-slate-400">
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: p.stroke || p.fill }}
          />
          {p.name}:{" "}
          <span className="font-mono-num text-slate-100">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="glass-panel rounded-xl border border-white/10 px-4 py-2.5 text-xs text-slate-200">
      <span
        className="mr-2 inline-block h-2 w-2 rounded-full"
        style={{ background: p.payload.fill }}
      />
      {p.name}:{" "}
      <span className="font-mono-num font-semibold">{fmt(p.value)}</span>
    </div>
  );
};

const MONTHS_TR = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];
const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({
      value: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`,
      label: `${MONTHS_TR[dt.getMonth()]} ${dt.getFullYear()}`,
    });
  }
  return out;
};

const Panel = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [reportMonth, setReportMonth] = useState(monthOptions()[0].value);
  const [mailBusy, setMailBusy] = useState(false);
  const emailReport = async () => {
    setMailBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/reports/email-me?month=${reportMonth}`,
      );
      toast.success(
        `${data.period} raporu ${data.email} adresine PDF olarak gönderildi.`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rapor e-postası gönderilemedi.");
    } finally {
      setMailBusy(false);
    }
  };
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("islemler");
  const [busy, setBusy] = useState(false);

  const [txForm, setTxForm] = useState({
    type: "gelir",
    amount: "",
    category: "Satış",
    description: "",
    account_id: "",
    contact_id: "",
    date: today(),
  });
  const [accForm, setAccForm] = useState({
    name: "",
    type: "Vadesiz TL",
    balance: "",
  });
  const [upForm, setUpForm] = useState({
    title: "",
    amount: "",
    type: "odeme",
    due_date: today(),
    account_id: "",
    contact_id: "",
  });

  const fetchAll = useCallback(async () => {
    const [s, t, a, u, c] = await Promise.all([
      axios.get(`${API}/panel/summary`),
      axios.get(`${API}/transactions`),
      axios.get(`${API}/accounts`),
      axios.get(`${API}/upcoming`),
      axios.get(`${API}/contacts`),
    ]);
    setSummary(s.data);
    setTxs(t.data);
    setAccounts(a.data);
    setUpcoming(u.data);
    setContacts(c.data);
  }, []);

  useEffect(() => {
    fetchAll().catch((e) => {
      if (e.response?.status === 401) navigate("/giris", { replace: true });
      else toast.error("Veriler yüklenemedi, sayfayı yenileyin.");
    });
  }, [fetchAll, navigate]);

  const logout = async () => {
    await logoutRequest();
    toast.success("Çıkış yapıldı.");
    navigate("/giris", { replace: true });
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız oldu.");
    } finally {
      setBusy(false);
    }
  };

  const addTx = (e) => {
    e.preventDefault();
    run(
      () =>
        axios.post(`${API}/transactions`, {
          ...txForm,
          amount: parseFloat(txForm.amount),
          account_id: txForm.account_id || null,
          contact_id: txForm.contact_id || null,
        }),
      "İşlem eklendi",
    ).then(() =>
      setTxForm({
        type: "gelir",
        amount: "",
        category: "Satış",
        description: "",
        account_id: "",
        contact_id: "",
        date: today(),
      }),
    );
  };

  const addAccount = (e) => {
    e.preventDefault();
    run(
      () =>
        axios.post(`${API}/accounts`, {
          ...accForm,
          balance: parseFloat(accForm.balance || "0"),
        }),
      "Hesap eklendi",
    ).then(() => setAccForm({ name: "", type: "Vadesiz TL", balance: "" }));
  };

  const addUpcoming = (e) => {
    e.preventDefault();
    run(
      () =>
        axios.post(`${API}/upcoming`, {
          ...upForm,
          amount: parseFloat(upForm.amount),
          account_id: upForm.account_id || null,
          contact_id: upForm.contact_id || null,
        }),
      "Kayıt eklendi",
    ).then(() =>
      setUpForm({
        title: "",
        amount: "",
        type: "odeme",
        due_date: today(),
        account_id: "",
        contact_id: "",
      }),
    );
  };

  const cards = summary
    ? [
        {
          label: "Toplam Bakiye",
          value: summary.total_balance,
          icon: Wallet,
          cls: "text-slate-50",
          testid: "summary-balance",
        },
        {
          label: "Bu Ay Gelir",
          value: summary.month_income,
          icon: TrendingUp,
          cls: "text-emerald-400",
          testid: "summary-income",
        },
        {
          label: "Bu Ay Gider",
          value: summary.month_expense,
          icon: TrendingDown,
          cls: "text-red-400",
          testid: "summary-expense",
        },
        {
          label: "Net Nakit Akışı",
          value: summary.net_flow,
          icon: Waves,
          cls: summary.net_flow >= 0 ? "text-emerald-300" : "text-red-300",
          testid: "summary-net",
        },
      ]
    : [];

  const monthPrefix = today().slice(0, 7);
  const catTotals = {};
  txs.forEach((t) => {
    if (t.type === "gider" && t.date.startsWith(monthPrefix)) {
      catTotals[t.category] = (catTotals[t.category] || 0) + t.amount;
    }
  });
  const donutData = Object.entries(catTotals)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  return (
    <main
      data-testid="panel-page"
      className="min-h-screen bg-[#07090E] pb-24 text-slate-50"
    >
      <header className="glass-panel sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link to="/" data-testid="panel-logo" aria-label="Akış ana sayfa">
            <Logo size={30} />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-3.5 py-1.5 text-xs font-medium text-emerald-400 sm:inline-flex">
              <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Veriler canlı kaydediliyor
            </span>
            <span
              data-testid="panel-user-chip"
              className="inline-flex max-w-[180px] items-center gap-2 truncate rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {user.has_logo ? (
                <img
                  src={logoUrl(user)}
                  alt=""
                  className="h-5 w-5 rounded-full object-cover"
                />
              ) : user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="h-5 w-5 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] font-bold text-emerald-300"
                >
                  {(user.name || user.email)[0].toUpperCase()}
                </span>
              )}
              <span className="truncate">
                {user.company || user.name || user.email}
              </span>
            </span>
            <BusinessSettings />
            <button
              data-testid="panel-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            {user.company || "Finans Paneli"}
          </div>
          <h1 className="font-display mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Merhaba {user.name?.split(" ")[0] || ""}, nakit akışın tek bakışta.
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-400 sm:text-base">
            Eklediğin her gelir ve gider yalnızca senin hesabında, kalıcı olarak
            saklanır; bakiyeler, grafik ve özetler anında güncellenir.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.testid}
              data-testid={c.testid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              className="rounded-2xl border border-white/8 bg-[#0D111A] p-5"
            >
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-500">
                <c.icon className={`h-3.5 w-3.5 ${c.cls}`} />
                {c.label}
              </div>
              <div
                className={`font-mono-num mt-2 text-xl font-semibold sm:text-2xl ${c.cls}`}
              >
                {fmt(c.value)}
              </div>
            </motion.div>
          ))}
        </div>

        {summary && (
          <div
            data-testid="summary-upcoming-strip"
            className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-white/8 bg-[#0D111A] px-5 py-4 text-sm"
          >
            <span className="flex items-center gap-2 text-slate-400">
              <CalendarClock className="h-4 w-4 text-amber-400" /> Yaklaşan:
            </span>
            <span className="font-mono-num text-emerald-400">
              +{fmt(summary.upcoming_in)} tahsilat
            </span>
            <span className="font-mono-num text-red-400">
              −{fmt(summary.upcoming_out)} ödeme
            </span>
            <span className="hidden h-4 w-px bg-white/10 sm:block" />
            <span className="flex items-center gap-2 text-slate-400">
              Cari:
            </span>
            <span
              data-testid="summary-receivables"
              className="font-mono-num text-emerald-300"
            >
              alacak {fmt(summary.receivables)}
            </span>
            <span
              data-testid="summary-payables"
              className="font-mono-num text-amber-300"
            >
              borç {fmt(summary.payables)}
            </span>
            <span className="ml-auto text-xs text-slate-600">
              {summary.tx_count} işlem · {summary.contact_count} cari
            </span>
          </div>
        )}

        {summary && (
          <motion.div
            data-testid="panel-chart"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 rounded-2xl border border-white/8 bg-[#0D111A] p-5 sm:p-7"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-semibold text-slate-100">
                Son 6 Ay — Nakit Akışı
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="report-month-select"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="rounded-full border border-white/12 bg-[#07090E] px-3 py-2 text-xs font-semibold text-slate-300 outline-none transition-all duration-300 focus:border-emerald-400/60"
                >
                  {monthOptions().map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <a
                  data-testid="download-pdf-btn"
                  href={`${API}/reports/cashflow.pdf?month=${reportMonth}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-emerald-400/50 hover:text-emerald-300"
                >
                  <FileDown className="h-3.5 w-3.5" /> PDF Rapor
                </a>
                <a
                  data-testid="download-xlsx-btn"
                  href={`${API}/reports/cashflow.xlsx?month=${reportMonth}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-emerald-400/50 hover:text-emerald-300"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Excel Rapor
                </a>
                <button
                  data-testid="email-report-btn"
                  onClick={emailReport}
                  disabled={mailBusy}
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                >
                  {mailBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5" />
                  )}{" "}
                  E-postayla Gönder
                </button>
              </div>
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={summary.monthly}
                  margin={{ top: 6, right: 8, left: -8, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="pGelir" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00FF87" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#00FF87" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="pGider" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#EF4444"
                        stopOpacity={0.28}
                      />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="rgba(255,255,255,0.05)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="ay"
                    stroke="#475569"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    stroke="#475569"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${Math.round(v / 1000)}K` : v
                    }
                  />
                  <Tooltip
                    content={<DarkTooltip />}
                    cursor={{ stroke: "rgba(16,185,129,0.3)" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="gelir"
                    name="Gelir"
                    stroke="#00FF87"
                    strokeWidth={2.5}
                    fill="url(#pGelir)"
                  />
                  <Area
                    type="monotone"
                    dataKey="gider"
                    name="Gider"
                    stroke="#EF4444"
                    strokeWidth={2}
                    fill="url(#pGider)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        <motion.div
          data-testid="expense-donut-card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 rounded-2xl border border-white/8 bg-[#0D111A] p-5 sm:p-7"
        >
          <h2 className="font-display text-lg font-semibold text-slate-100">
            Bu Ay — Gider Dağılımı
          </h2>
          {donutData.length > 0 ? (
            <div className="mt-4 grid items-center gap-8 sm:grid-cols-2">
              <div className="relative h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={64}
                      outerRadius={92}
                      paddingAngle={3}
                      stroke="none"
                    >
                      {donutData.map((d, i) => (
                        <Cell
                          key={d.name}
                          fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Toplam
                  </span>
                  <span className="font-mono-num text-lg font-bold text-slate-100">
                    {fmt(donutTotal)}
                  </span>
                </div>
              </div>
              <ul data-testid="donut-legend" className="space-y-2.5">
                {donutData.map((d, i) => (
                  <li
                    key={d.name}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="flex items-center gap-2.5 text-slate-300">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          background: DONUT_COLORS[i % DONUT_COLORS.length],
                        }}
                      />
                      {d.name}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono-num text-slate-100">
                        {fmt(d.value)}
                      </span>
                      <span className="font-mono-num w-12 text-right text-xs text-slate-500">
                        %
                        {((d.value / donutTotal) * 100)
                          .toFixed(1)
                          .replace(".", ",")}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-600">
              Bu ay için gider kaydı yok — bir gider eklediğinde dağılım burada
              görünür.
            </p>
          )}
        </motion.div>

        <div className="mt-10">
          <div className="inline-flex gap-1.5 rounded-full border border-white/8 bg-[#0D111A] p-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={t.testid}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
                  tab === t.id
                    ? "bg-emerald-500 text-emerald-950 shadow-[0_0_18px_rgba(0,255,135,0.3)]"
                    : "text-slate-400 hover:text-slate-100"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="mt-6"
            >
              {tab === "islemler" && (
                <div className="grid gap-6 lg:grid-cols-5">
                  <form
                    data-testid="add-transaction-form"
                    onSubmit={addTx}
                    className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-2"
                  >
                    <h3 className="font-display mb-5 flex items-center gap-2 text-base font-semibold text-slate-100">
                      <Plus className="h-4 w-4 text-emerald-400" /> Yeni İşlem
                    </h3>
                    <div className="mb-4 grid grid-cols-2 gap-2">
                      {["gelir", "gider"].map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`tx-type-${t}`}
                          onClick={() =>
                            setTxForm({
                              ...txForm,
                              type: t,
                              category: CATEGORIES[t][0],
                              contact_id: "",
                            })
                          }
                          className={`rounded-xl border py-2.5 text-sm font-semibold capitalize transition-all duration-300 ${
                            txForm.type === t
                              ? t === "gelir"
                                ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-300"
                                : "border-red-400/60 bg-red-500/15 text-red-300"
                              : "border-white/12 text-slate-500 hover:text-slate-300"
                          }`}
                        >
                          {t === "gelir" ? "Gelir" : "Gider"}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-3">
                      <input
                        data-testid="tx-amount-input"
                        type="number"
                        min="0.01"
                        step="0.01"
                        required
                        value={txForm.amount}
                        onChange={(e) =>
                          setTxForm({ ...txForm, amount: e.target.value })
                        }
                        placeholder="Tutar (₺)"
                        className={`${inputCls} w-full`}
                      />
                      <select
                        data-testid="tx-category-select"
                        value={txForm.category}
                        onChange={(e) =>
                          setTxForm({ ...txForm, category: e.target.value })
                        }
                        className={`${inputCls} w-full`}
                      >
                        {CATEGORIES[txForm.type].map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid="tx-description-input"
                        type="text"
                        value={txForm.description}
                        onChange={(e) =>
                          setTxForm({ ...txForm, description: e.target.value })
                        }
                        placeholder="Açıklama (isteğe bağlı)"
                        className={`${inputCls} w-full`}
                      />
                      <select
                        data-testid="tx-account-select"
                        value={txForm.account_id}
                        onChange={(e) =>
                          setTxForm({ ...txForm, account_id: e.target.value })
                        }
                        className={`${inputCls} w-full`}
                      >
                        <option value="">Hesap seç (bakiyeye işlenir)</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name} — {a.type}
                          </option>
                        ))}
                      </select>
                      <ContactSelect
                        testid="tx-contact-select"
                        value={txForm.contact_id}
                        contacts={contacts}
                        type={txForm.type === "gelir" ? "musteri" : "tedarikci"}
                        onChange={(v) =>
                          setTxForm({ ...txForm, contact_id: v })
                        }
                      />
                      <input
                        data-testid="tx-date-input"
                        type="date"
                        required
                        value={txForm.date}
                        onChange={(e) =>
                          setTxForm({ ...txForm, date: e.target.value })
                        }
                        className={`${inputCls} w-full [color-scheme:dark]`}
                      />
                      <button
                        data-testid="tx-submit-btn"
                        type="submit"
                        disabled={busy}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        İşlemi Kaydet
                      </button>
                    </div>
                  </form>

                  <div className="rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-3">
                    <h3 className="font-display mb-4 text-base font-semibold text-slate-100">
                      Son İşlemler
                    </h3>
                    <ul
                      data-testid="transactions-list"
                      className="divide-y divide-white/5"
                    >
                      {txs.slice(0, 12).map((t) => (
                        <li
                          key={t.id}
                          className="group flex items-center justify-between gap-3 py-3.5"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${t.type === "gelir" ? "bg-emerald-500/10" : "bg-red-500/10"}`}
                            >
                              {t.type === "gelir" ? (
                                <ArrowUpRight className="h-4 w-4 text-emerald-400" />
                              ) : (
                                <ArrowDownLeft className="h-4 w-4 text-red-400" />
                              )}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-slate-100">
                                {t.description || t.category}
                              </div>
                              <div className="mt-0.5 text-xs text-slate-500">
                                {t.category} ·{" "}
                                {new Date(
                                  t.date + "T00:00:00",
                                ).toLocaleDateString("tr-TR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                                {t.contact_id &&
                                  contacts.find(
                                    (c) => c.id === t.contact_id,
                                  ) && (
                                    <span className="text-slate-400">
                                      {" "}
                                      ·{" "}
                                      {
                                        contacts.find(
                                          (c) => c.id === t.contact_id,
                                        ).name
                                      }
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span
                              className={`font-mono-num text-sm font-semibold ${t.type === "gelir" ? "text-emerald-400" : "text-red-400"}`}
                            >
                              {t.type === "gelir" ? "+" : "−"}
                              {fmt(t.amount)}
                            </span>
                            <button
                              data-testid={`tx-delete-${t.id}`}
                              onClick={() =>
                                run(
                                  () =>
                                    axios.delete(`${API}/transactions/${t.id}`),
                                  "İşlem silindi",
                                )
                              }
                              className="text-slate-600 opacity-0 transition-all duration-300 hover:text-red-400 group-hover:opacity-100"
                              aria-label="İşlemi sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                      {txs.length === 0 && (
                        <li className="py-8 text-center text-sm text-slate-600">
                          Henüz işlem yok — ilk kaydını ekle.
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {tab === "hesaplar" && (
                <div className="grid gap-6 lg:grid-cols-5">
                  <form
                    data-testid="add-account-form"
                    onSubmit={addAccount}
                    className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-2"
                  >
                    <h3 className="font-display mb-5 flex items-center gap-2 text-base font-semibold text-slate-100">
                      <Plus className="h-4 w-4 text-emerald-400" /> Yeni Hesap
                    </h3>
                    <div className="space-y-3">
                      <input
                        data-testid="account-name-input"
                        type="text"
                        required
                        value={accForm.name}
                        onChange={(e) =>
                          setAccForm({ ...accForm, name: e.target.value })
                        }
                        placeholder="Banka / hesap adı"
                        className={`${inputCls} w-full`}
                      />
                      <select
                        data-testid="account-type-select"
                        value={accForm.type}
                        onChange={(e) =>
                          setAccForm({ ...accForm, type: e.target.value })
                        }
                        className={`${inputCls} w-full`}
                      >
                        {[
                          "Vadesiz TL",
                          "Vadeli TL",
                          "Döviz (USD)",
                          "Döviz (EUR)",
                          "POS Bloke",
                          "Cari Hesap",
                        ].map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid="account-balance-input"
                        type="number"
                        step="0.01"
                        value={accForm.balance}
                        onChange={(e) =>
                          setAccForm({ ...accForm, balance: e.target.value })
                        }
                        placeholder="Güncel bakiye (₺)"
                        className={`${inputCls} w-full`}
                      />
                      <button
                        data-testid="account-submit-btn"
                        type="submit"
                        disabled={busy}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        Hesabı Ekle
                      </button>
                    </div>
                  </form>

                  <div className="grid h-fit gap-4 sm:grid-cols-2 lg:col-span-3">
                    {accounts.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`account-card-${a.id}`}
                        className="group rounded-2xl border border-white/8 bg-[#0D111A] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-400/40"
                      >
                        <div className="flex items-start justify-between">
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10">
                            <Building2 className="h-5 w-5 text-emerald-400" />
                          </span>
                          <button
                            data-testid={`account-delete-${a.id}`}
                            onClick={() =>
                              run(
                                () => axios.delete(`${API}/accounts/${a.id}`),
                                "Hesap silindi",
                              )
                            }
                            className="text-slate-600 opacity-0 transition-all duration-300 hover:text-red-400 group-hover:opacity-100"
                            aria-label="Hesabı sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-4 text-sm font-semibold text-slate-100">
                          {a.name}
                        </div>
                        <div className="text-xs text-slate-500">{a.type}</div>
                        <div className="font-mono-num mt-3 text-xl font-semibold text-slate-50">
                          {fmt(a.balance)}
                        </div>
                      </div>
                    ))}
                    {accounts.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-white/12 p-8 text-center text-sm text-slate-600 sm:col-span-2">
                        Henüz hesap yok — ilk hesabını ekle.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === "yaklasan" && (
                <div className="grid gap-6 lg:grid-cols-5">
                  <form
                    data-testid="add-upcoming-form"
                    onSubmit={addUpcoming}
                    className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-2"
                  >
                    <h3 className="font-display mb-5 flex items-center gap-2 text-base font-semibold text-slate-100">
                      <Plus className="h-4 w-4 text-emerald-400" /> Yeni Ödeme /
                      Tahsilat
                    </h3>
                    <div className="mb-4 grid grid-cols-2 gap-2">
                      {["odeme", "tahsilat"].map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`up-type-${t}`}
                          onClick={() =>
                            setUpForm({ ...upForm, type: t, contact_id: "" })
                          }
                          className={`rounded-xl border py-2.5 text-sm font-semibold transition-all duration-300 ${
                            upForm.type === t
                              ? t === "tahsilat"
                                ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-300"
                                : "border-amber-400/60 bg-amber-500/15 text-amber-300"
                              : "border-white/12 text-slate-500 hover:text-slate-300"
                          }`}
                        >
                          {t === "odeme" ? "Ödeme" : "Tahsilat"}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-3">
                      <input
                        data-testid="up-title-input"
                        type="text"
                        required
                        value={upForm.title}
                        onChange={(e) =>
                          setUpForm({ ...upForm, title: e.target.value })
                        }
                        placeholder="Başlık (ör. Kira ödemesi)"
                        className={`${inputCls} w-full`}
                      />
                      <input
                        data-testid="up-amount-input"
                        type="number"
                        min="0.01"
                        step="0.01"
                        required
                        value={upForm.amount}
                        onChange={(e) =>
                          setUpForm({ ...upForm, amount: e.target.value })
                        }
                        placeholder="Tutar (₺)"
                        className={`${inputCls} w-full`}
                      />
                      <ContactSelect
                        testid="up-contact-select"
                        value={upForm.contact_id}
                        contacts={contacts}
                        type={
                          upForm.type === "tahsilat" ? "musteri" : "tedarikci"
                        }
                        onChange={(v) =>
                          setUpForm({ ...upForm, contact_id: v })
                        }
                      />
                      <select
                        data-testid="up-account-select"
                        value={upForm.account_id}
                        onChange={(e) =>
                          setUpForm({ ...upForm, account_id: e.target.value })
                        }
                        className={`${inputCls} w-full`}
                      >
                        <option value="">
                          Tamamlanınca işlenecek hesap (isteğe bağlı)
                        </option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name} — {a.type}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid="up-date-input"
                        type="date"
                        required
                        value={upForm.due_date}
                        onChange={(e) =>
                          setUpForm({ ...upForm, due_date: e.target.value })
                        }
                        className={`${inputCls} w-full [color-scheme:dark]`}
                      />
                      <button
                        data-testid="up-submit-btn"
                        type="submit"
                        disabled={busy}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        Kaydı Ekle
                      </button>
                    </div>
                  </form>

                  <div className="h-fit rounded-2xl border border-white/8 bg-[#0D111A] p-6 lg:col-span-3">
                    <h3 className="font-display mb-4 text-base font-semibold text-slate-100">
                      Yaklaşan Ödemeler & Tahsilatlar
                    </h3>
                    <ul
                      data-testid="upcoming-list"
                      className="divide-y divide-white/5"
                    >
                      {upcoming.map((u) => (
                        <li
                          key={u.id}
                          className={`group flex items-center justify-between gap-3 py-3.5 ${u.done ? "opacity-45" : ""}`}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <button
                              data-testid={`up-toggle-${u.id}`}
                              onClick={() =>
                                run(
                                  () => axios.patch(`${API}/upcoming/${u.id}`),
                                  u.done
                                    ? "Tekrar açıldı — işlem geri alındı"
                                    : "Tamamlandı — işlem kaydedildi",
                                )
                              }
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-all duration-300 ${
                                u.done
                                  ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-300"
                                  : "border-white/15 text-transparent hover:border-emerald-400/50"
                              }`}
                              aria-label="Durumu değiştir"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <div className="min-w-0">
                              <div
                                className={`truncate text-sm font-medium ${u.done ? "text-slate-500 line-through" : "text-slate-100"}`}
                              >
                                {u.title}
                              </div>
                              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                                <CalendarClock className="h-3 w-3" />
                                {new Date(
                                  u.due_date + "T00:00:00",
                                ).toLocaleDateString("tr-TR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                                · {u.type === "odeme" ? "Ödeme" : "Tahsilat"}
                                {u.contact_id &&
                                  contacts.find(
                                    (c) => c.id === u.contact_id,
                                  ) && (
                                    <span className="truncate text-slate-400">
                                      ·{" "}
                                      {
                                        contacts.find(
                                          (c) => c.id === u.contact_id,
                                        ).name
                                      }
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span
                              className={`font-mono-num text-sm font-semibold ${u.type === "tahsilat" ? "text-emerald-400" : "text-amber-400"}`}
                            >
                              {u.type === "tahsilat" ? "+" : "−"}
                              {fmt(u.amount)}
                            </span>
                            <button
                              data-testid={`up-delete-${u.id}`}
                              onClick={() =>
                                run(
                                  () => axios.delete(`${API}/upcoming/${u.id}`),
                                  "Kayıt silindi",
                                )
                              }
                              className="text-slate-600 opacity-0 transition-all duration-300 hover:text-red-400 group-hover:opacity-100"
                              aria-label="Kaydı sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                      {upcoming.length === 0 && (
                        <li className="py-8 text-center text-sm text-slate-600">
                          Yaklaşan kayıt yok — yeni bir ödeme veya tahsilat
                          ekle.
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {tab === "cari" && (
                <ContactsTab contacts={contacts} busy={busy} run={run} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
};

export default Panel;
